import { FC, useEffect } from "react";
import { Box } from "@material-ui/core";
import { FormikBag, FormikProps, withFormik } from "formik";
import * as yup from "yup";

import { Button, NumberField, UsdIntegerInput } from "components";
import { formatCurrencyAmount } from "lib/format";
import { parseNumber } from "lib/parse";
import { Dictionary } from "services/utils";

export interface FormValues {
  minimumPrice: string;
}

interface FormProps {
  onSave: (values: FormValues) => Promise<void>;
  initialValues: FormValues;
  fieldErrors: Dictionary<keyof FormValues, string>;
  buyNowPrice: number | null;
  isLoading: boolean;
}

const EditMinimumPriceForm: FC<FormProps & FormikProps<FormValues>> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  setErrors,
  isSubmitting,
  fieldErrors,
  isLoading,
}) => {
  useEffect(() => {
    setErrors(fieldErrors);
  }, [fieldErrors, setErrors]);

  return (
    <form onSubmit={handleSubmit}>
      <Box my={2} textAlign="center">
        <Box my={2}>
          <NumberField
            type="text"
            name="minimumPrice"
            fullWidth
            label="Minimum Bid"
            value={values.minimumPrice}
            helperText={touched.minimumPrice && errors.minimumPrice}
            disabled={isSubmitting}
            onBlur={handleBlur}
            onChange={handleChange}
            error={touched.minimumPrice && !!errors.minimumPrice}
            inputComponent={UsdIntegerInput}
          />
        </Box>
        <Box my={2}>
          <Button
            customColor="green"
            variant="contained"
            size="large"
            fullWidth
            type="submit"
            disabled={isSubmitting || isLoading}
          >
            Set Minimum Bid
          </Button>
        </Box>
      </Box>
    </form>
  );
};

const formikOptions = {
  mapPropsToValues: (props: FormProps) => props.initialValues,

  validationSchema: (props: FormProps) => {
    const { buyNowPrice } = props;

    return yup.object().shape({
      minimumPrice: yup
        .string()
        .test(
          "less than list price",
          `The minimum bid must be lower than the list price (${formatCurrencyAmount(
            "USD",
            buyNowPrice,
          )})`,
          amount =>
            !amount || !buyNowPrice || buyNowPrice > parseNumber(amount, 2),
        )
        .required("Required"),
    });
  },
  handleSubmit: async (
    values: FormValues,
    { props }: FormikBag<FormProps, FormValues>,
  ) => {
    try {
      await props.onSave(values);
    } catch {
      // pass
    }
  },
};

const EnhancedEditMinimumPriceForm = withFormik<FormProps, FormValues>(
  formikOptions,
)(EditMinimumPriceForm);

export default EnhancedEditMinimumPriceForm;
